import React from 'react';
import type { TFunction } from 'i18next';
import type { LocaleCode } from '@/api/models';
import { type AccountTreeNode } from '../../utils';

/** DataGrid tags each tree-column cell wrapper with this slot. */
const TREE_CELL_SELECTOR = '[data-slot="data-grid-tree-cell"]';
/**
 * The expand/collapse toggle specifically — DataGrid tags it `slot="chevron"`.
 * Scoping to it matters: the tree cell also hosts our own row-action buttons
 * (Edit / Reactivate), and a bare `button` lookup grabbed the Edit button on
 * leaf rows (which have no chevron), announcing it as "Expand {account}".
 */
const CHEVRON_SELECTOR = 'button[slot="chevron"]';
/** Literal prefix of the account-name cell's test id (see `AccountTree`). */
const NAME_TESTID_PREFIX = 'chart-of-accounts-name-';

/**
 * Names each group row's expand/collapse chevron for screen readers.
 *
 * HeroUI Pro's `DataGrid` renders the chevron with a hardcoded, generic
 * `aria-label` ("Collapse row" / "Expand row") and exposes no prop to override
 * it per row. So — exactly like the scoped `.coa-data-grid` CSS, which keys off
 * DataGrid's internal `data-*`/`slot` attributes because the grid gives no
 * per-row className — we relabel the buttons from the committed DOM.
 *
 * Why a MutationObserver rather than a plain effect: React Aria's Table mounts
 * its tree rows in a second pass (after our effect has already run) and rewrites
 * the chevron's `aria-label` back to the generic string on its own re-renders.
 * A one-shot effect therefore sees zero rows and never reapplies. The observer
 * relabels whenever rows appear or a label is reset, disconnecting while it
 * writes so its own mutations don't re-trigger it. It touches **only** elements
 * matching {@link CHEVRON_SELECTOR} — the row-action buttons that share the tree
 * cell name themselves and must be left alone. The effect re-subscribes when
 * anything the label depends on changes — expansion (`expandedKeys`), the
 * visible tree (`nodesById`, fresh whenever the tree is rebuilt for
 * search/data/locale), or the active language (`locale`/`t`) — so labels track
 * toggles and language switches. Each chevron is mapped back to its account via
 * the name cell's test id, and the expanded state is read from the controlled
 * `expandedKeys` (the source of truth).
 */
export const useChevronAriaLabels = (
    gridRef: React.RefObject<HTMLElement | null>,
    nodesById: Map<string, AccountTreeNode>,
    expandedKeys: Set<string>,
    locale: LocaleCode,
    t: TFunction,
): void => {
    React.useEffect(() => {
        const root = gridRef.current;
        if (!root || typeof MutationObserver === 'undefined') {
            return;
        }

        const relabel = (): void => {
            const cells =
                root.querySelectorAll<HTMLElement>(TREE_CELL_SELECTOR);
            cells.forEach((cell) => {
                // Leaf rows render a spacer instead of a toggle — nothing to
                // name. Row-action buttons in the same cell are never matched:
                // they carry no `slot="chevron"` and name themselves.
                const button =
                    cell.querySelector<HTMLButtonElement>(CHEVRON_SELECTOR);
                if (!button) {
                    return;
                }

                const nameEl = cell.querySelector<HTMLElement>(
                    `[data-testid^="${NAME_TESTID_PREFIX}"]`,
                );
                const id = nameEl?.dataset.testid?.slice(
                    NAME_TESTID_PREFIX.length,
                );
                const node = id ? nodesById.get(id) : undefined;
                if (!node) {
                    return;
                }

                const name = node.account.name;
                const key = expandedKeys.has(node.account.id)
                    ? 'chartOfAccounts.tree.collapse'
                    : 'chartOfAccounts.tree.expand';
                const label = t(key, { name });
                if (button.getAttribute('aria-label') !== label) {
                    button.setAttribute('aria-label', label);
                }
            });
        };

        const observer = new MutationObserver(() => {
            // Detach while writing so our own `aria-label` edits don't re-arm
            // the observer (bounded, but avoids needless churn).
            observer.disconnect();
            relabel();
            observer.observe(root, OBSERVE_OPTIONS);
        });

        relabel();
        observer.observe(root, OBSERVE_OPTIONS);

        return () => observer.disconnect();
    }, [gridRef, nodesById, expandedKeys, locale, t]);
};

/**
 * Watch for the two ways a chevron's name can go stale: rows mounting or
 * re-rendering (`childList`) and DataGrid resetting the `aria-label` on its own
 * re-render (the filtered `attributes`).
 */
const OBSERVE_OPTIONS: MutationObserverInit = {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['aria-label'],
};
